import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import NoControlsVideo from '../NoControlsVideo';
import { isEmbeddedWebView } from '../../utils/inAppBrowser';

/**
 * The step screens both web2app funnels show — /funnel-auth (FunnelAuthPage)
 * and its intro entry (/funnel-auth/intro, the same page with the explainer first):
 * pick a friend → meet them → name → sign in.
 *
 * Presentation only: each page keeps its own state, analytics and API calls
 * and passes them in, and renders these inside its own card and step header.
 * One copy of the markup and the `fs-` styles means the two funnels can't
 * drift apart visually. Pages render <FunnelScreenStyles /> once; their own
 * one-off screens (waits, invalid link, welcome back) use the same `fs-` type
 * and button classes.
 */

export const MIN_NAME_LENGTH = 2;

// "Daniel's", used in the sign-in headline
const possessive = (name) => `${name}'s`;

// ── icons ──────────────────────────────────────────────────────────────────
// Drawn inline rather than with the app's <Icon>, which renders glyphs from
// the Material Symbols web font: a large download, and until it lands the
// browser shows nothing (or the icon's name) in its place. Same glyphs —
// Material Symbols Outlined paths — so nothing changes but the wait.

const SymbolIcon = ({ d, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
    <path d={d} />
  </svg>
);

export const PhotoLibraryIcon = ({ size }) => (
  <SymbolIcon
    size={size}
    d="M360-400h400L622-580l-92 120-62-80-108 140Zm-40 160q-33 0-56.5-23.5T240-320v-480q0-33 23.5-56.5T320-880h480q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H320Zm0-80h480v-480H320v480ZM160-80q-33 0-56.5-23.5T80-160v-560h80v560h560v80H160Zm160-720v480-480Z"
  />
);

export const CameraIcon = ({ size }) => (
  <SymbolIcon
    size={size}
    d="M480-260q75 0 127.5-52.5T660-440q0-75-52.5-127.5T480-620q-75 0-127.5 52.5T300-440q0 75 52.5 127.5T480-260Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29ZM160-120q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h126l74-80h240l74 80h126q33 0 56.5 23.5T880-680v480q0 33-23.5 56.5T800-120H160Zm0-80h640v-480H638l-73-80H395l-73 80H160v480Zm320-240Z"
  />
);

export const CheckIcon = ({ size }) => (
  <SymbolIcon size={size} d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
);

// ── decorations ────────────────────────────────────────────────────────────

export const StarOutline = ({ className = '' }) => (
  <svg className={`fs-deco ${className}`} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 2.8l2.75 5.6 6.15.9-4.45 4.34 1.05 6.13L12 16.87l-5.5 2.9 1.05-6.13L3.1 9.3l6.15-.9L12 2.8z"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"
    />
  </svg>
);

// The two short strokes drawn beside a character, like a comic "pop"
export const MotionLines = ({ className = '' }) => (
  <svg className={`fs-deco ${className}`} viewBox="0 0 40 40" aria-hidden="true">
    <path d="M13 22L22 5M22 30L36 20" fill="none" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" />
  </svg>
);

export const Sparkle = ({ className = '' }) => (
  <svg className={`fs-deco ${className}`} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 1.5C12.8 7.2 16.8 11.2 22.5 12 16.8 12.8 12.8 16.8 12 22.5 11.2 16.8 7.2 12.8 1.5 12 7.2 11.2 11.2 7.2 12 1.5z"
      fill="currentColor"
    />
  </svg>
);

export const Chevron = () => (
  <svg className="fs-chevron" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Shared by the generating and finishing steps of both funnels. The poster is
// the clip's first frame, so a browser that won't autoplay (iOS Low Power
// Mode) still shows the panda rather than an empty glow.
export const LoadingPandaVideo = ({ className }) => (
  <NoControlsVideo
    className={className}
    autoPlay
    muted
    loop
    poster="/assets/loading-panda-poster.png"
    animated="/assets/loading-panda-loop.webp"
  >
    <source src="/assets/loading-panda.webm" type="video/webm" />
    <source src="/assets/loading-panda.mp4" type="video/mp4" />
  </NoControlsVideo>
);

const GoogleIcon = () => (
  <svg className="fs-google-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// ── shared clip helpers ────────────────────────────────────────────────────

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Plays a clip from the top. Muted, so browsers let it start without a
// tap — except iOS Low Power Mode, where it quietly stays on the still.
export const playClip = (video) => {
  if (!video || prefersReducedMotion()) return;
  try {
    video.currentTime = 0;
  } catch (_) {
    // not seekable yet; it's at the start anyway
  }
  const attempt = video.play();
  // Older engines return undefined rather than a promise
  if (attempt?.catch) attempt.catch(() => {});
};

// Transparent video takes a different codec per engine: WebKit (Safari, and
// every browser on iOS) plays HEVC with alpha; Chromium and Firefox play VP9
// with alpha. Neither shows the other's transparency, so the file is picked
// here rather than left to <source> order — Chromium can claim HEVC support
// and would then play it without its transparency.
const APPLE_WEBKIT = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iOS = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return iOS || (/Safari\//.test(ua) && !/Chrome|Chromium|Edg|OPR|Firefox|Android/.test(ua));
})();
export const alphaSrc = (base) => `${base}.${APPLE_WEBKIT ? 'mov' : 'webm'}`;

// ── background ─────────────────────────────────────────────────────────────
// The night sky behind every step after the welcome screen: a short clip cut
// to loop without a seam (its end crossfades into its start). The first frame
// is the poster, so the sky is there at once — and stays, still, wherever
// autoplay is off (iOS Low Power Mode, reduced motion).
//
// Sign-in has its own sky. `scene="moon"` dissolves into a clip that drifts up
// through the same stars while a full moon rises, and holds on the moon; going
// back dissolves home again. It's fetched a step early (`moonSoon`). Where it
// can't play, the moon is simply there — its last frame is the poster.

const MOON_RISE_RATE = 1.25;
const MOON_FIRST = '/assets/funnel/moon-rise-first.webp';
const MOON_LAST = '/assets/funnel/moon-rise-last.webp';

export const FunnelSky = ({ scene = 'night', moonSoon = false }) => {
  const nightRef = useRef(null);
  const moonRef = useRef(null);
  const moon = scene === 'moon';
  const [moonLoaded, setMoonLoaded] = useState(moon || moonSoon);
  const [riseBlocked, setRiseBlocked] = useState(false);
  const reduced = prefersReducedMotion();

  useEffect(() => {
    if (moon || moonSoon) setMoonLoaded(true);
  }, [moon, moonSoon]);

  useEffect(() => {
    const night = nightRef.current;
    if (!moon) {
      // Home again: the loop was paused while the moon was up
      if (night?.paused && !prefersReducedMotion()) night.play()?.catch?.(() => {});
      return undefined;
    }
    const rise = moonRef.current;
    if (rise && !prefersReducedMotion()) {
      rise.defaultPlaybackRate = MOON_RISE_RATE;
      rise.playbackRate = MOON_RISE_RATE;
      try {
        rise.currentTime = 0;
      } catch (_) {
        // not seekable yet; it's at the start anyway
      }
      rise.play()?.catch?.(() => setRiseBlocked(true));
    }
    // Once the dissolve is over, the loop underneath needn't keep decoding
    const id = setTimeout(() => {
      if (night && !night.paused) night.pause();
    }, 900);
    return () => clearTimeout(id);
  }, [moon, moonLoaded]);

  return (
    <div className={`fs-sky${moon ? ' is-moon' : ''}`} aria-hidden="true">
      <NoControlsVideo
        ref={nightRef}
        className="fs-sky-video"
        src="/assets/funnel/funnel-sky.mp4"
        poster="/assets/funnel/funnel-sky-poster.webp"
        animated="/assets/funnel/funnel-sky-loop.webp"
        muted
        loop
        autoPlay={!reduced}
        preload="auto"
      />
      {moonLoaded && (
        <NoControlsVideo
          ref={moonRef}
          className="fs-sky-video fs-sky-moon"
          src="/assets/funnel/moon-rise.mp4"
          poster={reduced || riseBlocked || isEmbeddedWebView() ? MOON_LAST : MOON_FIRST}
          muted
          preload="auto"
        />
      )}
    </div>
  );
};

// ── name ───────────────────────────────────────────────────────────────────
// Asked after the meet step, right before sign-in.
// Pip points down at the name field, then cheers once there's a name in it.
// The cheer clip opens on the pose the pointing one ends on, so it only takes
// over once the pointing has finished — never mid-gesture — with a quick
// crossfade over the seam.

export const PIP_POSTER = '/assets/funnel/pip-point-poster.webp';

const usePip = (hasName) => {
  const pointRef = useRef(null);
  const cheerRef = useRef(null);
  const [cheering, setCheering] = useState(false);
  // Set the moment the cheer starts. On the animated-image path (TikTok's
  // browser) mounting the image is what plays it, so it's mounted from here.
  const [cheerLive, setCheerLive] = useState(false);
  const pointDone = useRef(false);
  const cheerQueued = useRef(false);
  const cheerStarted = useRef(false);

  const startCheer = useCallback(() => {
    if (cheerStarted.current) return;
    cheerStarted.current = true;
    setCheerLive(true);
    playClip(cheerRef.current);
  }, []);

  // Run from the field's change handler, i.e. inside a keystroke. A cheer
  // that has to wait for the pointing to finish is started and paused here,
  // because iOS Low Power Mode only lets a video start from a gesture — once
  // it has, it can be played again later.
  const queueCheer = useCallback(() => {
    if (cheerQueued.current || prefersReducedMotion()) return;
    cheerQueued.current = true;
    // An animated image never reports ending, so there the cheer doesn't wait
    if (pointDone.current || isEmbeddedWebView()) {
      startCheer();
      return;
    }
    const video = cheerRef.current;
    const attempt = video?.play();
    attempt?.then?.(() => {
      if (cheerStarted.current) return;
      video.pause();
      video.currentTime = 0;
    }).catch(() => {});
  }, [startCheer]);

  // A name already there (coming back to this step) cheers after the point
  useEffect(() => {
    if (hasName) queueCheer();
  }, [hasName, queueCheer]);

  return {
    pointRef,
    cheerRef,
    cheering,
    cheerLive,
    queueCheer,
    onPointEnded: () => {
      pointDone.current = true;
      if (cheerQueued.current) startCheer();
    },
    // The warm-up play above fires this too; only the real start shows it
    onCheerPlaying: () => {
      if (cheerStarted.current) setCheering(true);
    },
  };
};

// Pip, pointing down at a name field; `cheer` (a name has been typed) makes
// him cheer once the pointing has finished. `queueCheer` is returned through
// `onReady` so the field's change handler can start the cheer from inside the
// keystroke (see usePip). The website onboarding shares this exact mascot.
export const PipMascot = ({ cheer = false, onReady }) => {
  const pip = usePip(cheer);
  const { queueCheer } = pip;
  useEffect(() => {
    onReady?.(queueCheer);
  }, [onReady, queueCheer]);
  return (
    <div className={`fs-pip${pip.cheering ? ' is-cheering' : ''}`} role="img" aria-label="Pixley Panda">
      <NoControlsVideo
        ref={pip.pointRef}
        className="fs-pip-clip fs-pip-point"
        src={alphaSrc('/assets/funnel/pip-point')}
        poster={PIP_POSTER}
        animated="/assets/funnel/pip-point-loop.webp"
        muted
        autoPlay={!prefersReducedMotion()}
        preload="auto"
        onEnded={pip.onPointEnded}
        aria-hidden="true"
      />
      <NoControlsVideo
        ref={pip.cheerRef}
        className="fs-pip-clip fs-pip-cheer"
        src={alphaSrc('/assets/funnel/pip-cheer')}
        animated={pip.cheerLive ? '/assets/funnel/pip-cheer-loop.webp' : undefined}
        muted
        preload="auto"
        onPlaying={pip.onCheerPlaying}
        aria-hidden="true"
      />
    </div>
  );
};

export const FunnelNameStep = ({ childName, onChangeName, onSubmit, itemVariants }) => {
  const valid = childName.trim().length >= MIN_NAME_LENGTH;
  const queueCheer = useRef(() => {});
  const onPipReady = useCallback((fn) => {
    queueCheer.current = fn;
  }, []);
  return (
    <>
      <motion.div variants={itemVariants} className="fs-mascot">
        <StarOutline className="fs-star fs-mascot-star-l" />
        <StarOutline className="fs-star fs-mascot-star-r" />
        <MotionLines className="fs-lines fs-mascot-lines" />
        <PipMascot cheer={childName.trim().length > 0} onReady={onPipReady} />
      </motion.div>
      <motion.div variants={itemVariants} className="fs-eyebrow">Now, make it personal</motion.div>
      <motion.h1 variants={itemVariants} className="fs-title">Who are we making this for?</motion.h1>
      <motion.p variants={itemVariants} className="fs-sub">
        A first name or nickname helps make their story world feel like theirs.
      </motion.p>
      <motion.div variants={itemVariants} className="fs-field-wrap">
        <label className="fs-field">
          <span className="fs-field-label">First name or nickname</span>
          <input
            className="fs-field-input"
            type="text"
            value={childName}
            maxLength={24}
            placeholder="e.g. Daniel"
            onChange={(e) => {
              if (e.target.value.trim()) queueCheer.current();
              onChangeName(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && valid) onSubmit('enter_key');
            }}
          />
        </label>
        <p className="fs-field-help">Use a nickname if you prefer.</p>
      </motion.div>
      <motion.div variants={itemVariants}>
        <button className="fs-cta" disabled={!valid} onClick={() => onSubmit('button')}>
          Continue <Chevron />
        </button>
      </motion.div>
    </>
  );
};

// ── pick a friend (or a photo) ─────────────────────────────────────────────
// `pick` is { type: 'friend', key } or { type: 'photo' }: one choice at a time.
// Tapping a friend only selects it; the button is what commits, so the art
// can be compared side by side before anything happens.

// Gold sparkles that pop out around a card when it's tapped
const BURST = ['a', 'b', 'c', 'd'];

// The friend already selected when the step opens waves once it has slid in
const ARRIVAL_WAVE_DELAY_MS = 450;

// Touch fires pointer enter/leave around every tap; only a real hover counts
const isHover = (e) => e.pointerType !== 'touch';

/* One ready-made friend, who comes to life in a looping clip
   (/assets/funnel-friends/<key>.mp4, a wave or a head turn — built to play
   forward and back, so the loop has no seam). The clip's first frame is the
   still, so it takes over without a jump — and only once it's actually
   playing, so a slow load just leaves the still up.
   The selected friend keeps looping; the one selected when the step opens
   starts by itself. Hovering another friend plays it until the pointer
   leaves. A tap starts it from the gesture itself, which even Low Power Mode
   lets play. */
const FriendCard = ({ friend, selected, disabled, onTap }) => {
  const videoRef = useRef(null);
  const [alive, setAlive] = useState(false);
  const [burst, setBurst] = useState(0);

  const startClip = () => {
    if (videoRef.current?.paused) playClip(videoRef.current);
  };
  const stopClip = () => {
    setAlive(false);
    const video = videoRef.current;
    if (video && !video.paused) video.pause();
  };

  const selectedOnArrival = useRef(selected);
  useEffect(() => {
    if (!selectedOnArrival.current) return undefined;
    const id = setTimeout(() => playClip(videoRef.current), ARRIVAL_WAVE_DELAY_MS);
    return () => clearTimeout(id);
  }, []);

  // Another choice was made: back to the still
  useEffect(() => {
    if (selected) return;
    setAlive(false);
    const video = videoRef.current;
    if (video && !video.paused) video.pause();
  }, [selected]);

  // Already playing (it was hovered, or it's the pick) — carry on, no restart
  const handleTap = () => {
    onTap(friend.key);
    if (prefersReducedMotion()) return;
    setBurst((b) => b + 1);
    startClip();
  };

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={`${friend.name} the ${friend.kind}`}
      className={`fs-friend${selected ? ' is-selected' : ''}${alive ? ' is-alive' : ''}`}
      onClick={handleTap}
      onPointerEnter={(e) => isHover(e) && startClip()}
      onPointerLeave={(e) => isHover(e) && !selected && stopClip()}
      disabled={disabled}
    >
      <span className="fs-friend-art">
        <img
          src={`/assets/funnel-friends/${friend.key}.webp`}
          alt=""
          width="120"
          height="120"
          // Fall back to the full-size PNG the rest of the app uses if
          // WebP 404s or fails to decode
          onError={(e) => {
            e.target.src = `/assets/${friend.key}.png`;
          }}
        />
        <NoControlsVideo
          ref={videoRef}
          className="fs-friend-clip"
          src={`/assets/funnel-friends/${friend.key}.mp4`}
          animated={selected ? `/assets/funnel-friends/${friend.key}-loop.webp` : undefined}
          muted
          loop
          preload="auto"
          onPlaying={() => setAlive(true)}
          aria-hidden="true"
        />
      </span>
      {selected && burst > 0 && (
        <span key={burst} className="fs-friend-burst" aria-hidden="true">
          {BURST.map((k) => <Sparkle key={k} className={`fs-gold fs-burst-${k}`} />)}
        </span>
      )}
      <span className="fs-friend-name">{friend.name}</span>
    </button>
  );
};

export const FunnelFriendStep = ({
  friends,
  pick,
  onPickFriend,
  onPickPhoto,
  photoPreviewUrl,
  fileInputRef,
  cameraInputRef,
  onPhotoChange,
  onOpenCamera,
  loadingFriend,
  onConfirm,
  itemVariants,
}) => {
  const pickedFriend = pick?.type === 'friend' ? friends.find((f) => f.key === pick.key) : null;
  const photoPicked = pick?.type === 'photo' && !!photoPreviewUrl;

  let ctaLabel = 'Choose a friend';
  if (pickedFriend) ctaLabel = `Meet ${pickedFriend.name}`;
  else if (photoPicked) ctaLabel = 'Bring them to life';

  return (
    <>
      <motion.div variants={itemVariants} className="fs-eyebrow">One easy choice</motion.div>
      <motion.h1 variants={itemVariants} className="fs-title">
        Pick their first friend.
      </motion.h1>
      <motion.p variants={itemVariants} className="fs-sub">
        Start with a ready-made friend. A photo or drawing is optional.
      </motion.p>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => onPhotoChange(e)}
        style={{ display: 'none' }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={(e) => onPhotoChange(e, 'camera_native')}
        style={{ display: 'none' }}
      />

      {photoPreviewUrl ? (
        <motion.div variants={itemVariants}>
          <button
            type="button"
            className={`fs-photo-card${photoPicked ? ' is-selected' : ''}`}
            // First tap switches back to the photo; once it's the pick, a tap
            // swaps it for another one
            onClick={photoPicked ? () => fileInputRef.current?.click() : onPickPhoto}
          >
            <img src={photoPreviewUrl} alt="What you added" className="fs-photo-thumb" />
            <span className="fs-photo-text">
              <strong>Photo added</strong>
              <small>{photoPicked ? 'Tap to change' : 'Tap to use this photo'}</small>
            </span>
            {photoPicked && <span className="fs-photo-check"><CheckIcon size={16} /></span>}
          </button>
        </motion.div>
      ) : (
        <motion.div variants={itemVariants} className="fs-photo-options">
          <button type="button" className="fs-photo-option" onClick={() => fileInputRef.current?.click()}>
            <PhotoLibraryIcon size={34} />
            <strong>Upload photo</strong>
          </button>
          <button type="button" className="fs-photo-option" onClick={onOpenCamera}>
            <CameraIcon size={34} />
            <strong>Take photo</strong>
          </button>
        </motion.div>
      )}

      <motion.div variants={itemVariants} className="fs-divider">
        <span>Or pick a ready-made friend</span>
      </motion.div>

      <motion.div variants={itemVariants} className="fs-friends" role="radiogroup" aria-label="Ready-made friends">
        {friends.map((friend) => (
          <FriendCard
            key={friend.key}
            friend={friend}
            selected={pickedFriend?.key === friend.key}
            disabled={!!loadingFriend}
            onTap={onPickFriend}
          />
        ))}
      </motion.div>

      <motion.div variants={itemVariants}>
        <button
          className="fs-cta fs-cta-spaced"
          disabled={(!pickedFriend && !photoPicked) || !!loadingFriend}
          onClick={onConfirm}
          aria-busy={!!loadingFriend}
        >
          {loadingFriend ? <span className="fs-cta-spinner" aria-label="Loading" /> : <>{ctaLabel} <Chevron /></>}
        </button>
      </motion.div>
    </>
  );
};

// ── hand-lettered title (meet, sign-in) ────────────────────────────────────
// A lead-in line, then the part that matters — bigger, with a yellow marker
// stroke drawn in under it. Without a `hero`, the lead is the whole title.

// `lead` is the first line, `hero` the big marker-underlined word(s), and
// `tail` anything that follows the hero on its line ("Mia is in!").
export const HandTitle = ({ lead, hero, tail, itemVariants }) => (
  <motion.h1 variants={itemVariants} className="fs-title fs-hand-title">
    {lead && <span className="fs-hand-lead">{lead}</span>}
    {hero && (
      <>
        {lead ? ' ' : null}
        <span className="fs-hand-name">
          {hero}
          <svg className="fs-hand-underline" viewBox="0 0 200 14" preserveAspectRatio="none" aria-hidden="true">
            <path d="M5 9 C 45 5, 80 6, 110 7.5 S 170 10, 195 6" pathLength="1" />
          </svg>
        </span>
        {tail && <span className="fs-hand-tail"> {tail}</span>}
      </>
    )}
  </motion.h1>
);

// ── meet ───────────────────────────────────────────────────────────────────
// `friend` is the ready-made friend they picked. Without one (made from a
// photo, or the page reloaded so it isn't known) the wording fits either.
//
// A ready-made friend comes to life in the hero: its full-size clip
// (/assets/funnel-friends/<key>-hero.mp4) starts looping as the card pops in,
// taking over from the still only once it's playing.

// Whether a character image is a cut-out (alpha channel, no backdrop) that can
// stand on a stage, or a picture to show in a frame. The page encodes the
// generated character as a PNG data URL only when it really has transparency
// (FunnelAuthPage's blobToDataURL `alpha`, which falls back to JPEG for an
// opaque render); the ready-made friends are opaque JPEGs; a character
// restored by URL is unknown, so it is framed.
export const isCutoutImage = (src) => typeof src === 'string' && src.startsWith('data:image/png');

// Lets the pop land before the wave starts
const HERO_WAVE_DELAY_MS = 350;

export const FunnelMeetStep = ({
  friend,
  previewImage,
  heroRevealed,
  onSave,
  itemVariants,
}) => {
  const clipRef = useRef(null);
  const [heroAlive, setHeroAlive] = useState(false);

  useEffect(() => {
    if (!heroRevealed || !friend) return undefined;
    const id = setTimeout(() => playClip(clipRef.current), HERO_WAVE_DELAY_MS);
    return () => clearTimeout(id);
  }, [heroRevealed, friend]);

  // Who they're meeting is the hero of the title
  const [lead, met] = friend ? ['Meet', `${friend.name}!`] : ['Meet their', 'story friend!'];
  // A character made from a photo is a cut-out: it stands on a small moonlit
  // stage inside the ring instead of filling the frame like a picture
  const cutout = !friend && isCutoutImage(previewImage);

  return (
    <>
      <motion.div variants={itemVariants} className="fs-eyebrow">Meet their story friend</motion.div>
      <HandTitle lead={lead} hero={met} itemVariants={itemVariants} />
      <motion.p variants={itemVariants} className="fs-sub">
        This is the beginning of the stories you'll make together.
      </motion.p>

      <div className={`fs-hero${heroRevealed ? ' revealed' : ''}${heroAlive ? ' is-alive' : ''}${cutout ? ' is-cutout' : ''}`}>
        <span className="fs-hero-ring" aria-hidden="true" />
        {cutout && <span className="fs-hero-stage" aria-hidden="true" />}
        <img src={previewImage} alt={friend?.name || 'Their character'} className="fs-hero-img" />
        {friend && (
          <NoControlsVideo
            ref={clipRef}
            className="fs-hero-clip"
            src={`/assets/funnel-friends/${friend.key}-hero.mp4`}
            animated={heroRevealed ? `/assets/funnel-friends/${friend.key}-hero-loop.webp` : undefined}
            muted
            loop
            preload="auto"
            onPlaying={() => setHeroAlive(true)}
            aria-hidden="true"
          />
        )}
      </div>

      <motion.div variants={itemVariants}>
        <button className="fs-cta" onClick={onSave}>
          Make it theirs <Chevron />
        </button>
      </motion.div>
    </>
  );
};

// ── sign in ────────────────────────────────────────────────────────────────
// Not a card: the moonlit sky opens up top (FunnelSky's moon scene) and a
// white sheet rises from the bottom with the words and the button. A
// ready-made friend stands on the sheet's edge in front of the moon — their
// clip, cut out of its backdrop, played once (it ends in the pose it starts
// in, so they wave and settle) — and a character made from a photo stands
// there the same way when it is a cut-out (isCutoutImage), or is framed when
// it is a picture.

// Width ÷ height of each friend's cut-out loop — cropped tight, feet on the
// bottom edge — so the space is held before the clip arrives
const FRIEND_CUTOUTS = { benny: 0.7533, 'zippy-icon': 0.8033, willow: 0.6067 };

// The page's step wrapper only fades for this step; the sheet and the friend
// make their own entrances
export const sheetStepVariants = {
  enter: { opacity: 1 },
  center: { opacity: 1 },
  exit: { opacity: 0, transition: { duration: 0.25, ease: 'easeIn' } },
};
export const sheetVariants = {
  enter: { y: 180, opacity: 0 },
  center: {
    y: 0,
    opacity: 1,
    transition: {
      y: { type: 'spring', stiffness: 150, damping: 22 },
      opacity: { duration: 0.3 },
      staggerChildren: 0.07,
      delayChildren: 0.22,
    },
  },
};
// ...and the friend lands on it once it's there
export const standVariants = {
  enter: { opacity: 0, y: 30, scale: 0.88 },
  center: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 190, damping: 15, delay: 0.38 },
  },
};

/**
 * The moonlit stage the sign-in sheet (and the paywall after it) stands the
 * character on: a ready-made friend's cut-out clip, a character made from a
 * photo standing there too when it is a cut-out (isCutoutImage) or framed when
 * it is a picture, or the panda when there's neither. Lands with standVariants
 * once the sheet below it is up.
 */
export const FunnelCharacterStage = ({ friend, previewImage, name = '', compact = false, snug = false }) => {
  const cutout = friend && FRIEND_CUTOUTS[friend.key];
  return (
    <div className={`fs-signin-stage${compact ? ' is-compact' : ''}${snug && !compact ? ' is-snug' : ''}`}>
      {cutout ? (
        <motion.div
          variants={standVariants}
          className="fs-signin-friend"
          style={{ aspectRatio: cutout }}
          role="img"
          aria-label={friend.name}
        >
          <NoControlsVideo
            className="fs-signin-friend-clip"
            src={alphaSrc(`/assets/funnel-friends/${friend.key}-cutout`)}
            poster={`/assets/funnel-friends/${friend.key}-cutout.webp`}
            animated={`/assets/funnel-friends/${friend.key}-cutout-loop.webp`}
            muted
            autoPlay={!prefersReducedMotion()}
            preload="auto"
            aria-hidden="true"
          />
        </motion.div>
      ) : isCutoutImage(previewImage) ? (
        <motion.div variants={standVariants} className="fs-signin-made">
          <img src={previewImage} alt={name || 'Their character'} />
        </motion.div>
      ) : previewImage ? (
        <motion.div variants={standVariants} className="fs-signin-frame">
          <img src={previewImage} alt={name || 'Their character'} />
        </motion.div>
      ) : (
        <motion.img
          variants={standVariants}
          className="fs-signin-mascot"
          src="/assets/pixley-panda-nobg.png"
          alt="Pixley Panda"
        />
      )}
    </div>
  );
};

const MailIcon = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect x="3.5" y="7" width="25" height="18" rx="4.5" stroke="currentColor" strokeWidth="2.6" />
    <path d="M5 9.5l9.2 7.1a3 3 0 0 0 3.6 0L27 9.5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const clock = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

// What each refusal from /auth/email-link/send says when the backend sent no message of its own
const SEND_ERRORS = {
  invalid_email: "That email doesn't look right. Check it and try again.",
  too_many: "We've sent a few links already. Check your inbox and spam, or try again later.",
  busy: "We can't send links right now. Try Google, or try again in a few minutes.",
  handoff: "We couldn't save your progress just now. Try again.",
  network: "You look offline. Check your connection and try again.",
};
const sendErrorText = (result) => result?.error || SEND_ERRORS[result?.code]
  || "We couldn't send the email just now. Try again, or use Google.";

const looksLikeEmail = (v) => /^[^\s@<>()[\],;:"]+@[^\s@<>()[\],;:"]+\.[A-Za-z]{2,}$/.test(v) && !v.includes('..');

/**
 * "Email me a sign-in link": an address field and one button. onSend(email) resolves to the API's answer
 * ({ok, code, error, data: {resend_after}}); a sent link hands the address up (onSent) so the step can show
 * the "check your email" panel.
 */
export const EmailLinkForm = ({ onSend, onSent, initialEmail = '', autoFocus = false }) => {
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const value = email.trim();
    if (!value) { setError('Enter your email first.'); inputRef.current?.focus(); return; }
    if (!looksLikeEmail(value)) { setError(SEND_ERRORS.invalid_email); inputRef.current?.focus(); return; }
    setSending(true);
    setError(null);
    const result = await onSend(value);
    setSending(false);
    if (result?.ok) onSent(value, result.data?.resend_after || 45);
    else setError(sendErrorText(result));
  };

  return (
    <form className="fs-email" onSubmit={submit} noValidate>
      <label className="fs-email-label" htmlFor="fs-email-input">Your email</label>
      <input
        id="fs-email-input"
        ref={inputRef}
        className={`fs-email-input${error ? ' has-error' : ''}`}
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder="you@example.com"
        value={email}
        aria-invalid={!!error}
        aria-describedby={error ? 'fs-email-error' : 'fs-email-help'}
        onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
      />
      {error
        ? <p id="fs-email-error" className="fs-email-error" role="alert">{error}</p>
        : <p id="fs-email-help" className="fs-email-help">No password. We'll email you a link that signs you in.</p>}
      <button type="submit" className="fs-cta fs-cta-email" disabled={sending}>
        {sending ? <><span className="fs-cta-spinner" aria-hidden="true" /> Sending…</> : <><MailIcon size={22} /> Email me a sign-in link</>}
      </button>
    </form>
  );
};

/** After the link went out: where it went, what to do, a resend timer and a way to fix a typo. */
export const EmailLinkSent = ({ email, resendAfter = 45, onResend, onChangeEmail }) => {
  const [left, setLeft] = useState(resendAfter);
  const [status, setStatus] = useState(null);   // null | 'sending' | 'resent' | error text
  useEffect(() => { setLeft(resendAfter); }, [resendAfter, email]);
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);

  const resend = async () => {
    if (left > 0 || status === 'sending') return;
    setStatus('sending');
    const result = await onResend(email);
    if (result?.ok) { setStatus('resent'); setLeft(result.data?.resend_after || 45); }
    else setStatus(sendErrorText(result));
  };

  return (
    <div className="fs-sent" role="status" aria-live="polite">
      <div className="fs-sent-icon"><MailIcon /></div>
      <p className="fs-sent-title">Check your email</p>
      <p className="fs-sent-body">
        We sent a sign-in link to <strong>{email}</strong>. Open it on this phone and you'll pick up right here.
      </p>
      <p className="fs-sent-tip">Can't find it? Check Spam or Promotions.{status === 'resent' ? ' Use the newest email.' : ''}</p>
      <button type="button" className="fs-sent-resend" onClick={resend} disabled={left > 0 || status === 'sending'}>
        {status === 'sending' ? 'Sending…' : left > 0 ? `Resend link in ${clock(left)}` : 'Resend link'}
      </button>
      {status && status !== 'sending' && status !== 'resent' && <p className="fs-email-error" role="alert">{status}</p>}
      <button type="button" className="fs-textlink" onClick={onChangeEmail}>Use a different email</button>
    </div>
  );
};

/** Shown above the sign-in options when an emailed link didn't work (expired, used, or an outage). */
export const EmailLinkNotice = ({ notice }) => (
  <div className="fs-link-notice" role="alert">
    <span className="fs-link-notice-dot" aria-hidden="true">!</span>
    <span>{notice.code === 'link_expired'
      ? 'That sign-in link expired or was already used. Send yourself a new one below.'
      : notice.message || "That sign-in link didn't work. Send yourself a new one below."}</span>
  </div>
);

/**
 * The in-app browser tip: points at the app's own menu (Instagram / Facebook put ••• at the top right), whose
 * "Open in external browser" takes this page, and the hand-off in its address, to Safari or Chrome.
 */
export const OpenInBrowserHint = ({ menu = '•••', browser = 'Safari', onDismiss }) => (
  <div className="fs-iab-hint" role="note">
    <span className="fs-iab-hint-caret" aria-hidden="true" />
    <span className="fs-iab-hint-icon" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M9.5 2.5h4v4M13.5 2.5L7.5 8.5M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    <div className="fs-iab-hint-text">
      <strong>Faster in {browser}</strong>
      {/* Instagram's item reads "Open in external browser"; the short form fits one line on a 375px phone */}
      <span>Tap <b className="fs-iab-hint-dots">{menu}</b> → Open in browser</span>
    </div>
    <button type="button" className="fs-iab-hint-close" onClick={onDismiss} aria-label="Close tip">
      <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
    </button>
  </div>
);

export const FunnelSigninStep = ({
  childName,
  friend,
  previewImage,
  isSigningIn,
  onGoogleSignIn,
  itemVariants,
  emailLink = null,      // {show, onSend, notice}: the email sign-in link (in-app browsers, or after a link failed)
}) => {
  const name = childName.trim();
  const whose = name ? possessive(name) : 'their';
  const heading = `Save ${whose} stories and ${friend?.name || 'character'}`;
  const body = "Sign in to keep what you've made and pick up where you left off.";
  const [sent, setSent] = useState(null);       // {email, resendAfter} once a link went out
  const [lastEmail, setLastEmail] = useState('');
  const showEmail = !!(emailLink && (emailLink.show || emailLink.notice));

  return (
    <div className="fs-signin">
      {/* Room for the email option: the character steps back a little */}
      <FunnelCharacterStage friend={friend} previewImage={previewImage} name={name} snug={showEmail && !sent} />

      <motion.div variants={sheetVariants} className={`fs-signin-sheet${showEmail && !sent ? ' has-email' : ''}`}>
        <motion.div variants={itemVariants} className="fs-eyebrow">{sent ? 'Almost there' : "Save what you've made"}</motion.div>
        {sent ? (
          <EmailLinkSent
            email={sent.email}
            resendAfter={sent.resendAfter}
            onResend={emailLink.onSend}
            onChangeEmail={() => { setLastEmail(sent.email); setSent(null); }}
          />
        ) : (
          <>
            <HandTitle lead={heading} itemVariants={itemVariants} />
            <motion.p variants={itemVariants} className="fs-sub">{body}</motion.p>

            {isSigningIn ? (
              <div className="fs-signing-in">
                <span className="fs-spinner" />
                <span>Setting up your account…</span>
              </div>
            ) : (
              <motion.div variants={itemVariants} className="fs-signin-actions">
                {emailLink?.notice && <EmailLinkNotice notice={emailLink.notice} />}
                <button className="fs-google" onClick={onGoogleSignIn}>
                  <GoogleIcon />
                  Continue with Google
                </button>
                {showEmail && (
                  <>
                    <div className="fs-or" aria-hidden="true"><span>or</span></div>
                    <EmailLinkForm
                      initialEmail={lastEmail}
                      autoFocus={!!lastEmail}
                      onSend={emailLink.onSend}
                      onSent={(email, resendAfter) => setSent({ email, resendAfter })}
                    />
                  </>
                )}
                <p className="fs-legal">
                  By continuing, you agree to Pixley's{' '}
                  <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                </p>
              </motion.div>
            )}
          </>
        )}

        <motion.p variants={itemVariants} className="fs-signin-wordmark" aria-hidden="true">PIXLEY</motion.p>
      </motion.div>
    </div>
  );
};

// ── styles ─────────────────────────────────────────────────────────────────
// Palette sampled from the design: ink #162642, accent #296FFB→#3E8DFD,
// link #3273EB, star outlines #AAC4FF, gold #FEC23C, hairlines #E4E9F5.

export const FunnelScreenStyles = () => (
  <style>{`
    /* Type */
    .fs-eyebrow {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13px; font-weight: 700;
      letter-spacing: 0.15em; text-transform: uppercase;
      color: #2869FC;
      margin: 0 0 12px;
      text-wrap: balance;
    }
    .fs-title {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 32px; font-weight: 700;
      line-height: 1.1; letter-spacing: -0.02em;
      color: #162642;
      margin: 0 0 12px;
      text-wrap: balance;
    }
    .fs-accent {
      background: linear-gradient(92deg, #2466F8 0%, #3E8DFD 100%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      -webkit-box-decoration-break: clone;
      box-decoration-break: clone;
    }
    .fs-sub {
      font-family: 'Inter', sans-serif;
      font-size: 16.5px; line-height: 1.5;
      color: #6B7892;
      margin: 0 0 22px;
      text-wrap: pretty;
    }

    /* Buttons */
    .fs-cta {
      position: relative;
      width: 100%;
      min-height: 58px;
      display: flex; align-items: center; justify-content: center; gap: 12px;
      padding: 14px 22px;
      border: none; border-radius: 20px;
      background: linear-gradient(90deg, #296FFB 0%, #3E8DFD 100%);
      color: #FFF;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 20px; font-weight: 700; letter-spacing: 0.005em;
      cursor: pointer;
      box-shadow: 0 12px 26px rgba(41,111,251,0.26), inset 0 1px 0 rgba(255,255,255,0.22);
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms, opacity 0.2s;
    }
    .fs-cta:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 16px 32px rgba(41,111,251,0.34), inset 0 1px 0 rgba(255,255,255,0.22);
    }
    .fs-cta:active:not(:disabled) { transform: scale(0.98); }
    .fs-cta:disabled { opacity: 0.45; cursor: default; box-shadow: none; }
    .fs-cta-spaced { margin-top: 22px; }
    .fs-chevron { width: 18px; height: 18px; flex-shrink: 0; transition: transform 0.2s ease; }
    .fs-cta:hover:not(:disabled) .fs-chevron { transform: translateX(3px); }
    .fs-cta-spinner {
      width: 22px; height: 22px;
      border: 3px solid rgba(255,255,255,0.4);
      border-top-color: #FFF;
      border-radius: 50%;
      animation: fsSpin 0.75s linear infinite;
    }

    .fs-cta:focus-visible, .fs-friend:focus-visible,
    .fs-photo-option:focus-visible, .fs-photo-card:focus-visible, .fs-google:focus-visible {
      outline: 3px solid rgba(41,111,251,0.4);
      outline-offset: 2px;
    }

    /* Decorations */
    .fs-deco { position: absolute; pointer-events: none; }
    .fs-star { color: #AAC4FF; }
    .fs-lines { color: #5E90F7; }
    .fs-gold { color: #FEC23C; }

    /* Sky behind the steps (FunnelSky) */
    .fs-sky {
      position: fixed; inset: 0;
      z-index: 0;
      overflow: hidden;
      pointer-events: none;
      background: linear-gradient(180deg, #243EA0 0%, #637BE9 50%, #BFABF1 100%);
    }
    .fs-sky-video {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
    }
    /* The moon sky dissolves in over the night one (and back out) */
    .fs-sky-moon {
      object-position: 50% 20%;
      opacity: 0;
      transition: opacity 0.8s ease;
    }
    .fs-sky.is-moon .fs-sky-moon { opacity: 1; }
    /* Wide screens: rather than blow the portrait clip up to the window's
       width, it stands as a centred column feathered into the night sky, so
       the moon sits behind the friend just as it does on a phone */
    @media (min-aspect-ratio: 3/4) {
      .fs-sky-moon {
        left: 50%; right: auto;
        width: calc(100vh * 0.532);
        transform: translateX(-50%);
        -webkit-mask-image: linear-gradient(90deg, transparent, #000 22%, #000 78%, transparent);
        mask-image: linear-gradient(90deg, transparent, #000 22%, #000 78%, transparent);
      }
    }

    /* Mascot (name step, sign-in fallback) */
    .fs-mascot {
      position: relative;
      width: 250px; height: 172px;
      margin: 4px auto 18px;
    }
    .fs-mascot-img {
      position: absolute; left: 50%; top: 0;
      height: 100%; width: auto;
      transform: translateX(-50%);
      object-fit: contain;
      filter: drop-shadow(0 12px 18px rgba(22,38,66,0.12));
    }
    .fs-mascot-star-l { width: 40px; height: 40px; left: 8px; top: 60px; transform: rotate(-12deg); }
    .fs-mascot-star-r { width: 40px; height: 40px; right: 6px; top: 104px; transform: rotate(10deg); }
    .fs-mascot-lines { width: 44px; height: 44px; right: 26px; top: -2px; }
    .fs-mascot-solo { height: 150px; margin-bottom: 22px; }
    /* Pip: the two clips stacked. The cheer fades in over the pointing clip's
       last frame; the pointing clip goes only once the cheer is fully in, so
       the panda never turns see-through mid-fade — and never shows twice once
       the cheer starts moving. */
    .fs-pip {
      position: absolute; left: 50%; top: 0;
      height: 100%; aspect-ratio: 424 / 460;
      transform: translateX(-50%);
      filter: drop-shadow(0 12px 18px rgba(22,38,66,0.12));
    }
    .fs-pip-clip {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: contain;
    }
    .fs-pip-cheer { opacity: 0; transition: opacity 0.18s ease; }
    .fs-pip.is-cheering .fs-pip-cheer { opacity: 1; }
    .fs-pip.is-cheering .fs-pip-point { opacity: 0; transition: opacity 0s linear 0.2s; }

    /* Name field — label sits inside the box, above the value */
    .fs-field-wrap { text-align: left; margin-bottom: 24px; }
    .fs-field {
      display: block;
      padding: 13px 18px 12px;
      background: #F9FAFE;
      border: 1.5px solid #E4E9F5;
      border-radius: 18px;
      cursor: text;
      transition: border-color 0.2s, box-shadow 0.25s, background 0.2s;
    }
    .fs-field:focus-within {
      border-color: #296FFB;
      background: #FFF;
      box-shadow: 0 0 0 4px rgba(41,111,251,0.12);
    }
    .fs-field-label {
      display: block;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 14px; font-weight: 600;
      color: #7F8BA5;
      margin-bottom: 2px;
    }
    .fs-field-input {
      width: 100%;
      padding: 0;
      border: none; outline: none;
      background: transparent;
      font-family: 'Inter', sans-serif;
      font-size: 21px; font-weight: 500;
      color: #162642;
      -webkit-tap-highlight-color: transparent;
    }
    .fs-field-input::placeholder { color: #B3BDCF; font-weight: 400; }
    .fs-field-help {
      margin: 10px 0 0 6px;
      font-family: 'Inter', sans-serif;
      font-size: 14px; color: #6B7892;
    }

    /* Ready-made friends */
    .fs-friends {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      margin-top: 4px;
    }
    .fs-friend {
      position: relative;
      display: flex; flex-direction: column; align-items: center; gap: 10px;
      padding: 12px 8px 14px;
      background: #F2F6FD;
      border: 2px solid transparent;
      border-radius: 22px;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), border-color 0.2s, background 0.2s, box-shadow 0.2s;
    }
    .fs-friend:hover:not(:disabled) { transform: translateY(-2px); }
    .fs-friend:active:not(:disabled) { transform: scale(0.97); }
    .fs-friend.is-selected {
      background: #FFF;
      border-color: #296FFB;
      transform: translateY(-4px);
      box-shadow: 0 16px 32px rgba(41,111,251,0.22), 0 0 0 5px rgba(41,111,251,0.10);
    }
    .fs-friend.is-selected:active:not(:disabled) { transform: translateY(-4px) scale(0.97); }
    .fs-friend:disabled { cursor: default; }
    .fs-friend-art {
      position: relative;
      width: 100%;
      aspect-ratio: 5 / 6;
      border-radius: 40% / 34%;
      overflow: hidden;
      transition: box-shadow 0.3s ease;
    }
    .fs-friend.is-selected .fs-friend-art { box-shadow: 0 8px 18px rgba(22,38,66,0.10); }
    /* The clip sits exactly over the still, framed the same, and fades in
       once it's playing */
    .fs-friend-art img, .fs-friend-clip {
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
      transform: scale(1.08);
    }
    .fs-friend-clip {
      position: absolute; inset: 0;
      opacity: 0;
      transition: opacity 0.25s ease;
    }
    .fs-friend.is-alive .fs-friend-clip { opacity: 1; }
    .fs-friend-name {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 18px; font-weight: 700;
      color: #162642;
      transition: color 0.2s;
    }
    .fs-friend.is-selected .fs-friend-name { color: #296FFB; }

    .fs-friend-burst { position: absolute; inset: 0; pointer-events: none; }
    .fs-friend-burst .fs-deco {
      width: 16px; height: 16px;
      opacity: 0;
      animation: fsBurst 0.9s cubic-bezier(.2,.8,.2,1) forwards;
    }
    .fs-friend-burst .fs-burst-a { left: 8%; top: 14%; --bx: -12px; --by: -14px; }
    .fs-friend-burst .fs-burst-b { right: 10%; top: 30%; --bx: 14px; --by: -8px; animation-delay: 0.06s; }
    .fs-friend-burst .fs-burst-c { left: 4%; top: 58%; --bx: -14px; --by: 6px; animation-delay: 0.12s; width: 12px; height: 12px; }
    .fs-friend-burst .fs-burst-d { right: 6%; top: 66%; --bx: 12px; --by: 10px; animation-delay: 0.18s; width: 12px; height: 12px; }
    @keyframes fsBurst {
      0% { opacity: 0; transform: translate(0, 0) scale(0.2) rotate(0deg); }
      30% { opacity: 1; }
      100% { opacity: 0; transform: translate(var(--bx), var(--by)) scale(1.15) rotate(40deg); }
    }

    .fs-divider {
      display: flex; align-items: center; gap: 14px;
      margin: 24px 0 16px;
      font-family: 'Inter', sans-serif;
      font-size: 15px; font-weight: 500;
      color: #8A95AB;
    }
    .fs-divider::before, .fs-divider::after {
      content: ''; flex: 1; height: 1px;
      background: linear-gradient(90deg, transparent, #C9D4E6, transparent);
    }

    /* Photo options */
    .fs-photo-options {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    .fs-photo-option {
      display: flex; flex-direction: column; align-items: center; gap: 10px;
      padding: 22px 10px;
      background: #F8FAFE;
      border: 2px dashed #C9D6EE;
      border-radius: 20px;
      color: #2277FF;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: border-color 0.2s, background 0.2s, transform 0.18s cubic-bezier(.2,.8,.2,1);
    }
    .fs-photo-option:hover { border-color: #296FFB; background: #FFF; transform: translateY(-2px); }
    .fs-photo-option strong {
      font-family: 'Inter', sans-serif;
      font-size: 17px; font-weight: 600;
      color: #162642;
    }
    .fs-photo-card {
      width: 100%;
      display: flex; align-items: center; gap: 14px;
      padding: 12px 16px;
      background: #F8FAFE;
      border: 2px dashed #C9D6EE;
      border-radius: 20px;
      cursor: pointer;
      text-align: left;
      -webkit-tap-highlight-color: transparent;
      transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
    }
    .fs-photo-card.is-selected {
      background: #FFF;
      border: 2px solid #296FFB;
      box-shadow: 0 10px 24px rgba(41,111,251,0.16);
    }
    .fs-photo-thumb { width: 54px; height: 54px; border-radius: 14px; object-fit: cover; }
    .fs-photo-text { flex: 1; display: flex; flex-direction: column; font-family: 'Inter', sans-serif; }
    .fs-photo-text strong { font-size: 16px; color: #162642; }
    .fs-photo-text small { font-size: 13px; color: #6B7892; }
    .fs-photo-check {
      width: 26px; height: 26px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%;
      background: #296FFB; color: #FFF;
      animation: fsPop 0.4s cubic-bezier(.34,1.5,.5,1);
    }

    /* Meet — the reveal */
    .fs-hero {
      position: relative;
      width: min(100%, 304px);
      aspect-ratio: 0.92;
      margin: 4px auto 16px;
      padding: 7px;
      border-radius: 38px;
      background: #FFF;
      box-shadow: 0 14px 40px rgba(62,107,255,0.20), 0 0 0 1px rgba(22,38,66,0.04);
      transform: scale(0);
      opacity: 0;
    }
    .fs-hero.revealed { animation: fsHeroPop 0.7s cubic-bezier(0.34, 1.45, 0.5, 1) forwards; }
    /* The frame's colours flow round it, as on the sign-in ring. A big conic
       layer spins behind the picture, clipped to the rounded frame — spinning
       the frame itself would swing its corners.
       The frame arrives white; once the card has landed, the colour fills the
       border from 12 o'clock, clockwise, by sweeping a conic mask open. (Any
       mask also makes Safari honour the rounded clip around a spinning layer.
       Browsers without @property skip the sweep and show the ring at its end.) */
    @property --fs-ring-fill {
      syntax: '<angle>';
      inherits: false;
      initial-value: 0deg;
    }
    .fs-hero-ring {
      position: absolute; inset: 0;
      border-radius: inherit;
      overflow: hidden;
      isolation: isolate;
      --fs-ring-fill: 0deg;
      /* the 6deg soft leading edge, and 366deg to close it at the top */
      -webkit-mask-image: conic-gradient(#000 calc(var(--fs-ring-fill) - 6deg), transparent var(--fs-ring-fill));
      mask-image: conic-gradient(#000 calc(var(--fs-ring-fill) - 6deg), transparent var(--fs-ring-fill));
    }
    .fs-hero.revealed .fs-hero-ring {
      animation: fsRingFill 1.3s cubic-bezier(.45,0,.25,1) 0.55s forwards;
    }
    @keyframes fsRingFill { to { --fs-ring-fill: 366deg; } }
    .fs-hero-ring::before {
      content: '';
      position: absolute; inset: -50%;
      background: conic-gradient(from 210deg, #3E6BFF, #8D7BE6, #E3A58F, #F2BC8C, #7B8EF2, #3E6BFF);
    }
    .fs-hero.revealed .fs-hero-ring::before { animation: fsRingSpin 6s linear infinite; }
    .fs-hero-img, .fs-hero-clip {
      width: 100%; height: 100%;
      object-fit: cover;
      border-radius: 31px;
      display: block;
    }
    .fs-hero-img { position: relative; }
    /* Sized explicitly to the picture's box (inside the 7px frame): a video
       left to size itself would take its own 768px */
    .fs-hero-clip {
      position: absolute; top: 7px; left: 7px;
      width: calc(100% - 14px); height: calc(100% - 14px);
      opacity: 0;
      transition: opacity 0.25s ease;
    }
    .fs-hero.is-alive .fs-hero-clip { opacity: 1; }
    /* A cut-out character (made from a photo) stands on a small moonlit stage
       inside the ring — the sky's gradient, a soft ground shadow — rather than
       filling the frame like a picture: the frame's white would only paint
       back the backdrop the model left out. */
    .fs-hero-stage {
      position: absolute; inset: 7px;
      border-radius: 31px;
      overflow: hidden;
      background: linear-gradient(180deg, #243EA0 0%, #637BE9 55%, #BFABF1 100%);
    }
    .fs-hero-stage::after {
      content: '';
      position: absolute; left: 50%; bottom: 7%;
      width: 58%; height: 9%;
      transform: translateX(-50%);
      background: radial-gradient(closest-side, rgba(22,38,66,0.34), rgba(22,38,66,0));
    }
    .fs-hero.is-cutout .fs-hero-img {
      object-fit: contain;
      object-position: 50% 100%;
      padding: 7% 9% 9%;
      box-sizing: border-box;
      filter: drop-shadow(0 10px 14px rgba(8,16,64,0.30));
    }

    /* Meet title — hand-lettered in Caveat Brush; the name they're meeting is bigger,
       with a yellow marker stroke behind its bottom edge that draws itself in */
    .fs-title.fs-hand-title {
      font-family: 'Caveat Brush', 'Caveat', 'Fredoka', cursive;
      font-weight: 400;
      color: #162642;
      letter-spacing: 0;
      line-height: 1.02;
      margin-bottom: 14px;
    }
    .fs-hand-lead { display: block; font-size: 38px; }
    .fs-hand-tail { font-size: 54px; }
    .fs-hand-name {
      position: relative;
      isolation: isolate;
      display: inline-block;
      font-size: 54px;
      padding: 0 8px;
    }
    .fs-hand-underline {
      position: absolute; left: 0; bottom: 0;
      z-index: -1;
      width: 100%; height: 14px;
      overflow: visible;
    }
    .fs-hand-underline path {
      fill: none;
      stroke: #F9D56E;
      stroke-width: 6;
      stroke-linecap: round;
      stroke-dasharray: 1;
      stroke-dashoffset: 1;
      animation: fsMarker 0.55s cubic-bezier(.4,0,.2,1) 0.45s forwards;
    }
    @keyframes fsMarker { to { stroke-dashoffset: 0; } }

    /* Sign in — the moonlit stage up top, the sheet below (FunnelSigninStep).
       --stage-h scales with the screen's height, as the moon behind does. */
    .fs-signin {
      --stage-h: clamp(190px, 33vh, 320px);
      flex: 1;
      display: flex; flex-direction: column;
    }
    .fs-signin-stage {
      position: relative;
      z-index: 2;
      height: var(--stage-h);
      display: flex; align-items: flex-end; justify-content: center;
      pointer-events: none;
    }
    /* A soft shadow on the sheet where the friend stands */
    .fs-signin-stage::after {
      content: '';
      position: absolute; left: 50%; bottom: -14px;
      z-index: -1;
      width: min(210px, 56%); height: 26px;
      transform: translateX(-50%);
      background: radial-gradient(closest-side, rgba(22,38,66,0.24), rgba(22,38,66,0));
    }
    .fs-signin-stage.is-compact { --stage-h: clamp(110px, 14vh, 220px); }
    .fs-signin-stage.is-snug { --stage-h: clamp(150px, 23vh, 250px); }
    .fs-signin-sheet.has-email { padding-top: 28px; }
    .fs-signin-sheet.has-email .fs-hand-lead { font-size: 34px; }
    .fs-signin-sheet.has-email .fs-sub { margin-bottom: 4px; }
    .fs-signin-friend {
      position: relative;
      height: calc(var(--stage-h) * 0.94);
      margin-bottom: -12px; /* feet a little into the sheet, standing on it */
      transform-origin: 50% 100%;
    }
    .fs-signin-friend-clip {
      width: 100%; height: 100%;
      object-fit: contain;
      display: block;
    }
    .fs-signin-mascot {
      height: calc(var(--stage-h) * 0.82);
      width: auto;
      margin-bottom: -8px;
      transform-origin: 50% 100%;
    }
    /* A cut-out character made from a photo stands where a friend would —
       feet on the sheet's edge, over the stage's shadow — sized by height so
       tall and wide characters alike fit the stage */
    .fs-signin-made {
      position: relative;
      height: calc(var(--stage-h) * 0.9);
      max-width: min(76vw, 320px);
      margin-bottom: -10px;
      transform-origin: 50% 100%;
    }
    .fs-signin-made img {
      height: 100%; width: 100%;
      object-fit: contain;
      object-position: 50% 100%;
      display: block;
      filter: drop-shadow(0 12px 16px rgba(8,16,64,0.35));
    }
    .fs-signin-frame {
      width: calc(var(--stage-h) * 0.66);
      aspect-ratio: 0.92;
      margin-bottom: 26px;
      padding: 6px;
      background: #FFF;
      border-radius: 30px;
      box-shadow: 0 18px 44px rgba(8,16,64,0.38);
      transform-origin: 50% 100%;
    }
    .fs-signin-frame img {
      width: 100%; height: 100%;
      object-fit: cover;
      border-radius: 24px;
      display: block;
    }
    /* The sheet: its top is one soft arc, highest in the middle */
    .fs-signin-sheet {
      position: relative;
      z-index: 1;
      flex: 1;
      display: flex; flex-direction: column; align-items: center;
      text-align: center;
      padding: 36px 26px calc(20px + env(safe-area-inset-bottom, 0px));
      background: #FFF;
      border-radius: 50% 50% 0 0 / 46px 46px 0 0;
      box-shadow: 0 -12px 40px rgba(12,22,80,0.20);
    }
    .fs-signin-sheet > * { width: 100%; max-width: 440px; }
    .fs-signin-sheet .fs-hand-lead { font-size: 40px; }
    .fs-signin-sheet .fs-sub { margin-bottom: 26px; }
    .fs-signin-actions { margin-top: 20px; }
    .fs-signin-wordmark {
      margin: auto 0 0;
      padding-top: 28px;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 15px; font-weight: 700;
      letter-spacing: 6px;
      color: #A9B3EE;
    }
    .fs-google {
      width: 100%;
      min-height: 62px;
      display: flex; align-items: center; justify-content: center; gap: 14px;
      padding: 14px 22px;
      background: #FFF;
      border: 2px solid #E1E6F6;
      border-radius: 31px;
      font-family: 'Inter', sans-serif;
      font-size: 18px; font-weight: 600;
      color: #162642;
      cursor: pointer;
      box-shadow: 0 4px 14px rgba(22,38,66,0.05);
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 150ms cubic-bezier(.2,.8,.2,1), border-color 0.2s, box-shadow 0.2s;
    }
    .fs-google:hover { transform: translateY(-2px); border-color: rgba(41,111,251,0.3); box-shadow: 0 8px 20px rgba(41,111,251,0.12); }
    .fs-google:active { transform: scale(0.98); }
    .fs-google-icon { width: 24px; height: 24px; }
    /* 13px fits the line on a 390px phone; balance keeps narrower ones tidy */
    .fs-legal {
      margin: 18px 0 0;
      font-family: 'Inter', sans-serif;
      font-size: 13px; line-height: 1.5;
      color: #7F8BA5;
      text-wrap: balance;
    }
    .fs-legal a { color: #6B7892; text-decoration: underline; text-underline-offset: 2px; }
    .fs-legal a:hover { color: #296FFB; }
    /* Email sign-in link (in-app browsers) */
    .fs-or {
      display: flex; align-items: center; gap: 14px;
      margin: 18px 0 16px;
      font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
      letter-spacing: 0.08em; text-transform: uppercase; color: #A3ADC2;
    }
    .fs-or::before, .fs-or::after { content: ''; flex: 1; height: 1.5px; background: #E8ECF7; border-radius: 1px; }
    .fs-email { display: flex; flex-direction: column; align-items: stretch; text-align: left; }
    .fs-email-label {
      position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap;
    }
    .fs-email-input {
      width: 100%; min-height: 60px;
      padding: 0 22px;
      background: #F7F9FE;
      border: 2px solid #E1E6F6; border-radius: 20px;
      font-family: 'Inter', sans-serif; font-size: 17px; font-weight: 500; color: #162642;
      outline: none;
      -webkit-appearance: none; appearance: none;
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    }
    .fs-email-input::placeholder { color: #A3ADC2; font-weight: 400; }
    .fs-email-input:focus { background: #FFF; border-color: #296FFB; box-shadow: 0 0 0 4px rgba(41,111,251,0.14); }
    .fs-email-input.has-error { border-color: #E5484D; background: #FFF; }
    .fs-email-input.has-error:focus { box-shadow: 0 0 0 4px rgba(229,72,77,0.14); }
    .fs-email-help, .fs-email-error {
      margin: 8px 4px 14px;
      font-family: 'Inter', sans-serif; font-size: 13.5px; line-height: 1.45;
      text-align: center; text-wrap: balance;
    }
    .fs-email-help { color: #7F8BA5; }
    .fs-email-error { color: #C93A3F; font-weight: 500; }
    .fs-cta-email { font-size: 18px; min-height: 60px; }
    .fs-sent {
      display: flex; flex-direction: column; align-items: center;
      margin-top: 4px;
      animation: fsSentIn 420ms cubic-bezier(.2,.8,.2,1) both;
    }
    .fs-sent-icon {
      width: 74px; height: 74px; margin: 2px 0 16px;
      display: grid; place-items: center;
      color: #296FFB; background: #EAF1FF; border-radius: 26px;
      animation: fsMailBob 2.6s ease-in-out 600ms infinite;
    }
    .fs-sent-title {
      margin: 0 0 10px;
      font-family: 'Fredoka', 'Inter', sans-serif; font-size: 30px; font-weight: 700;
      line-height: 1.1; letter-spacing: -0.02em; color: #162642;
    }
    .fs-sent-body {
      margin: 0 0 8px;
      font-family: 'Inter', sans-serif; font-size: 16px; line-height: 1.5; color: #6B7892;
      text-wrap: pretty; overflow-wrap: anywhere;
    }
    .fs-sent-body strong { color: #162642; font-weight: 600; }
    .fs-sent-tip { margin: 0 0 22px; font-family: 'Inter', sans-serif; font-size: 13.5px; color: #8A96AC; }
    .fs-sent-resend {
      width: 100%; min-height: 56px;
      background: #FFF; border: 2px solid #E1E6F6; border-radius: 28px;
      font-family: 'Inter', sans-serif; font-size: 16.5px; font-weight: 600; color: #296FFB;
      font-variant-numeric: tabular-nums;
      cursor: pointer; -webkit-tap-highlight-color: transparent; touch-action: manipulation;
      transition: border-color 0.2s, transform 150ms cubic-bezier(.2,.8,.2,1);
    }
    .fs-sent-resend:hover:not(:disabled) { border-color: rgba(41,111,251,0.35); transform: translateY(-1px); }
    .fs-sent-resend:disabled { color: #A3ADC2; cursor: default; }
    .fs-textlink {
      margin-top: 14px; padding: 8px 12px;
      background: none; border: none;
      font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 600; color: #3273EB;
      cursor: pointer; text-decoration: underline; text-underline-offset: 3px; text-decoration-thickness: 1.5px;
    }
    .fs-link-notice {
      display: flex; align-items: flex-start; gap: 10px;
      margin: 0 0 16px; padding: 12px 14px;
      background: #FFF7E8; border: 1.5px solid #FFE0A6; border-radius: 16px;
      font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.45; color: #7A4B00; text-align: left;
    }
    .fs-link-notice-dot {
      flex: none; width: 20px; height: 20px; margin-top: 1px;
      display: grid; place-items: center;
      background: #FEC23C; color: #5C3900; border-radius: 50%;
      font-size: 13px; font-weight: 800;
    }
    /* Compact, tucked into the top-right corner under the app's ••• menu: clear of the back button and the
       character. Dressed like the page's own white controls (the back button, the Google button) */
    .fs-iab-hint {
      position: fixed; top: 8px; right: 8px; z-index: 60;
      display: flex; align-items: center; gap: 8px;
      /* up to the back button (16px in + 44px wide) with a 12px gap */
      max-width: min(310px, calc(100vw - 80px));
      padding: 7px 7px 7px 7px;
      background: #FFF;
      border: 2px solid #E1E6F6;
      border-radius: 18px;
      box-shadow: 0 10px 26px rgba(22,38,66,0.16);
      font-family: 'Inter', sans-serif; text-align: left;
      animation: fsHintIn 460ms cubic-bezier(.2,.8,.2,1) 900ms both;
    }
    .fs-iab-hint-caret {
      position: absolute; top: -8px; right: 20px;
      width: 12px; height: 12px;
      background: #FFF;
      border-top: 2px solid #E1E6F6; border-left: 2px solid #E1E6F6;
      border-top-left-radius: 3px;
      transform: rotate(45deg);
      animation: fsCaretNudge 1.6s ease-in-out 1.4s infinite;
    }
    .fs-iab-hint-icon {
      flex: none; width: 28px; height: 28px;
      display: grid; place-items: center;
      background: #EAF1FF; color: #296FFB; border-radius: 11px;
    }
    .fs-iab-hint-text { min-width: 0; display: flex; flex-direction: column; gap: 1px; font-size: 12.5px; line-height: 1.35; color: #6B7892; text-wrap: balance; }
    .fs-iab-hint-text strong { white-space: nowrap; }
    .fs-iab-hint-text strong { font-family: 'Fredoka', 'Inter', sans-serif; font-size: 15px; font-weight: 700; color: #162642; letter-spacing: 0.005em; }
    .fs-iab-hint-dots {
      display: inline-block; padding: 0 5px; margin: 0 1px;
      background: #EAF1FF; color: #296FFB; border-radius: 6px;
      font-weight: 800; letter-spacing: 1px; line-height: 1.4;
    }
    .fs-iab-hint-close {
      flex: none; width: 26px; height: 26px; margin-left: -2px;
      display: grid; place-items: center;
      background: #F2F5FC; border: none; border-radius: 50%;
      color: #7F8BA5; cursor: pointer;
    }
    .fs-iab-hint-close:hover { color: #162642; background: #E8EDF9; }
    @keyframes fsSentIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
    @keyframes fsMailBob { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-4px) rotate(-3deg); } }
    @keyframes fsHintIn { from { opacity: 0; transform: translateY(-8px) scale(0.96); } to { opacity: 1; transform: none; } }
    @keyframes fsCaretNudge { 0%, 100% { transform: translateY(0) rotate(45deg); } 50% { transform: translateY(-3px) rotate(45deg); } }
    @media (prefers-reduced-motion: reduce) {
      .fs-sent, .fs-sent-icon, .fs-iab-hint, .fs-iab-hint-caret { animation: none; }
    }
    .fs-signing-in {
      display: flex; align-items: center; justify-content: center; gap: 12px;
      padding: 16px;
      font-family: 'Inter', sans-serif;
      font-size: 16px; font-weight: 600; color: #162642;
    }
    .fs-spinner {
      width: 22px; height: 22px;
      border: 3px solid #DDE6F2;
      border-top-color: #296FFB;
      border-radius: 50%;
      animation: fsSpin 0.8s linear infinite;
    }

    @keyframes fsSpin { to { transform: rotate(360deg); } }
    @keyframes fsPop { from { transform: scale(0); } to { transform: scale(1); } }
    @keyframes fsRingSpin { to { transform: rotate(360deg); } }
    @keyframes fsHeroPop {
      0% { transform: scale(0); opacity: 0; }
      70% { transform: scale(1.05); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }

    /* Phones */
    @media (max-width: 480px) {
      .fs-hand-lead { font-size: 35px; }
      .fs-hand-name { font-size: 50px; }
      /* Sized so the longest eyebrow ("A character they can call their own")
         stays on one line on a 375px phone */
      .fs-eyebrow { font-size: 12.5px; letter-spacing: 0.14em; }
      .fs-title { font-size: 29px; }
      .fs-sub { font-size: 16px; }
      .fs-cta { font-size: 19px; }
      .fs-friends { gap: 8px; }
      .fs-friend { padding: 10px 6px 12px; border-radius: 20px; }
      .fs-friend-name { font-size: 17px; }
      .fs-photo-option { padding: 20px 8px; }
    }
    @media (max-width: 374px) {
      .fs-eyebrow { font-size: 11.5px; letter-spacing: 0.12em; }
      .fs-title { font-size: 26px; }
      .fs-friend-name { font-size: 15.5px; }
      .fs-photo-option strong { font-size: 15px; }
      .fs-mascot { width: 220px; height: 150px; }
      .fs-hero { width: min(100%, 262px); }
    }
    /* Short viewports: shrink the tall decorative pieces so the button stays in view */
    @media (max-height: 700px) {
      .fs-signin-sheet { padding-top: 28px; }
      .fs-signin-sheet .fs-hand-lead { font-size: 34px; }
      .fs-signin-sheet .fs-sub { margin-bottom: 18px; }
      .fs-signin-actions { margin-top: 12px; }
      .fs-signin-wordmark { padding-top: 18px; }
      .fs-hand-lead { font-size: 32px; }
      .fs-hand-name { font-size: 44px; }
      .fs-title { font-size: 26px; }
      .fs-sub { margin-bottom: 16px; }
      .fs-mascot { height: 128px; margin-bottom: 12px; }
      .fs-mascot-star-l { top: 40px; }
      .fs-mascot-star-r { top: 76px; }
      .fs-hero { width: min(100%, 220px); margin-bottom: 14px; }
      .fs-divider { margin: 18px 0 12px; }
    }

    /* The loading spinners keep turning — they're feedback, not decoration */
    @media (prefers-reduced-motion: reduce) {
      .fs-photo-check { animation: none; }
      .fs-hand-underline path { animation: none; stroke-dashoffset: 0; }
      .fs-friend-burst { display: none; }
      .fs-friend.is-selected { transform: none; }
      .fs-hero.revealed { animation: none; transform: scale(1); opacity: 1; }
      .fs-hero.revealed .fs-hero-ring { animation: none; --fs-ring-fill: 366deg; }
      .fs-hero.revealed .fs-hero-ring::before { animation: none; }
      .fs-cta:hover:not(:disabled), .fs-friend:hover:not(:disabled),
      .fs-photo-option:hover, .fs-google:hover { transform: none; }
    }
  `}</style>
);
